import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Plus, Eye, Users, CheckCircle, Clock, Star, Download, Trash2, BarChart2 }from 'lucide-react';

const MOCK_SURVEYS = [
  {
    id: 1, title: 'Satisfaction post-séjour — Marrakech', status: 'active',
    respondents: 24, avg_score: 4.6, created_at: '2026-01-10',
    questions: 8, type: 'satisfaction'
  },
  {
    id: 2, title: 'Évaluation bien-être aidant — J+30', status: 'active',
    respondents: 17, avg_score: 4.3, created_at: '2026-01-15',
    questions: 12, type: 'wellbeing'
  },
  {
    id: 3, title: 'Feedback Partenaire Agadir — Q1 2026', status: 'draft',
    respondents: 0, avg_score: null, created_at: '2026-02-01',
    questions: 6, type: 'partner'
  },
  {
    id: 4, title: 'Suivi longitudinal dyade — 3 mois', status: 'completed',
    respondents: 41, avg_score: 4.7, created_at: '2025-12-01',
    questions: 15, type: 'longitudinal'
  },
];

const MOCK_RESPONSES = [
  { name: 'Sophie M.', date: '2026-01-20', score: 5, comment: 'Séjour exceptionnel, ma mère était épanouie. Personnel très attentionné.' },
  { name: 'Jean-Pierre L.', date: '2026-01-18', score: 4, comment: 'Très satisfait dans l\'ensemble, quelques petits ajustements à prévoir.' },
  { name: 'Marie-Claire D.', date: '2026-01-15', score: 5, comment: 'Une expérience transformatrice. Je recommande vivement.' },
  { name: 'Ahmed B.', date: '2026-01-12', score: 4, comment: 'Organisation impeccable, communication claire tout au long du séjour.' },
];

const TYPE_LABELS = { satisfaction: 'Satisfaction', wellbeing: 'Bien-être', partner: 'Partenaire', longitudinal: 'Longitudinal' };
const TYPE_COLORS = { satisfaction: 'bg-[#FFF3E8] text-[#F47B20]', wellbeing: 'bg-green-50 text-green-700', partner: 'bg-blue-50 text-blue-700', longitudinal: 'bg-purple-50 text-purple-700' };

export default function SurveysModule() {
  const [surveys, setSurveys] = useState(MOCK_SURVEYS);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: '', type: 'satisfaction' });

  const totalRespondents = surveys.reduce((s, x) => s + x.respondents, 0);
  const avgScore = surveys.filter(s => s.avg_score).reduce((a, s, _, arr) => a + s.avg_score / arr.filter(x => x.avg_score).length, 0);

  const createSurvey = () => {
    if (!form.title.trim()) return toast.error('Titre requis');
    setSurveys([...surveys, { id: Date.now(), ...form, status: 'draft', respondents: 0, avg_score: null, created_at: new Date().toISOString().split('T')[0], questions: 0 }]);
    setShowNew(false);
    setForm({ title: '', type: 'satisfaction' });
    toast.success('Enquête créée (brouillon)');
  };

  const toggleStatus = (id) => {
    setSurveys(surveys.map(s => s.id === id ? { ...s, status: s.status === 'active' ? 'paused' : 'active' } : s));
    toast.success('Statut mis à jour');
  };

  const remove = (id) => {
    const s = surveys.find(x => x.id === id);
    if (!window.confirm(`Supprimer l'enquête « ${s.title} » ?`)) return;
    setSurveys(surveys.filter(x => x.id !== id));
    toast.success('Enquête supprimée');
  };

  return (
    <div className="p-6 space-y-6" data-testid="surveys-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-primary">Enquêtes Aidants</h2>
          <p className="text-sm text-muted-foreground font-body">Satisfaction, bien-être, suivi longitudinal des dyades</p>
        </div>
        <Button onClick={() => setShowNew(true)} className="rounded-full bg-accent hover:bg-accent/90 text-white gap-2 font-body" data-testid="surveys-new-btn">
          <Plus className="w-4 h-4" /> Nouvelle enquête
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Enquêtes actives', value: surveys.filter(s => s.status === 'active').length, Icon: CheckCircle, color: '#22863A' },
          { label: 'Total répondants', value: totalRespondents, Icon: Users, color: '#5A3B31' },
          { label: 'Score moyen', value: `${avgScore.toFixed(1)}/5`, Icon: Star, color: '#F47B20' },
          { label: 'En brouillon', value: surveys.filter(s => s.status === 'draft').length, Icon: Clock, color: '#8B5CF6' },
        ].map(({ label, value, Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl p-5 border border-border shadow-sm flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: color + '18' }}>
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <div>
              <p className="font-heading text-xl font-semibold text-primary">{value}</p>
              <p className="text-xs text-muted-foreground font-body">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Surveys list */}
      <div className="space-y-3">
        {surveys.map(s => (
          <div key={s.id} className="bg-white rounded-2xl border border-border shadow-sm p-5 hover:border-primary/20 transition-all" data-testid={`survey-item-${s.id}`}>
            <div className="flex items-start gap-4">
              {/* Type icon */}
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${TYPE_COLORS[s.type].replace('text-', 'text-').split(' ')[0]} bg-opacity-50`}
                style={{ background: s.type === 'satisfaction' ? '#FFF3E8' : s.type === 'wellbeing' ? '#F0FFF4' : s.type === 'partner' ? '#EFF6FF' : '#F5F3FF' }}>
                <BarChart2 className="w-4 h-4" style={{ color: s.type === 'satisfaction' ? '#F47B20' : s.type === 'wellbeing' ? '#16A34A' : s.type === 'partner' ? '#2563EB' : '#7C3AED' }} />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="font-heading font-semibold text-primary text-sm">{s.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[s.type]}`}>{TYPE_LABELS[s.type]}</span>
                  <Badge className={s.status === 'active' ? 'bg-green-100 text-green-700 text-xs' : s.status === 'completed' ? 'bg-blue-100 text-blue-700 text-xs' : 'bg-gray-100 text-gray-500 text-xs'}>
                    {s.status === 'active' ? 'Active' : s.status === 'completed' ? 'Terminée' : 'Brouillon'}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground font-body">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{s.respondents} réponses</span>
                  {s.avg_score && <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-[#F47B20] text-[#F47B20]" />{s.avg_score}/5</span>}
                  <span>{s.questions} questions</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Créée le {new Date(s.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {s.respondents > 0 && (
                  <button onClick={() => setSelected(s)} className="p-2 hover:bg-muted rounded-xl transition-colors" title="Voir les résultats" data-testid={`survey-view-${s.id}`}>
                    <Eye className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
                {s.status !== 'completed' && (
                  <button onClick={() => toggleStatus(s.id)} className="px-3 py-1.5 rounded-full text-xs font-body font-medium bg-muted text-muted-foreground hover:bg-muted/80 transition-all">
                    {s.status === 'active' ? 'Pause' : 'Activer'}
                  </button>
                )}
                <button onClick={() => remove(s.id)} className="p-2 hover:bg-red-50 rounded-xl transition-colors" title="Supprimer" data-testid={`survey-delete-${s.id}`}>
                  <Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {surveys.length === 0 && (
          <div className="text-center py-12 text-muted-foreground font-body text-sm">Aucune enquête. Créez-en une !</div>
        )}
      </div>

      {/* Results modal */}
      {selected && (
        <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">{selected.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Répondants', value: selected.respondents },
                  { label: 'Score moyen', value: `${selected.avg_score}/5` },
                  { label: 'Taux complétion', value: '82%' },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-background rounded-xl p-3 border border-border text-center">
                    <p className="font-heading text-lg font-semibold text-primary">{value}</p>
                    <p className="text-xs text-muted-foreground font-body">{label}</p>
                  </div>
                ))}
              </div>
              <h4 className="font-heading font-semibold text-sm text-primary">Dernières réponses</h4>
              <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                {MOCK_RESPONSES.map((r, i) => (
                  <div key={i} className="bg-background rounded-xl p-4 border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium font-body">{r.name}</span>
                      <div className="flex items-center gap-1">
                        {[1,2,3,4,5].map(n => <Star key={n} className={`w-3.5 h-3.5 ${n <= r.score ? 'fill-[#F47B20] text-[#F47B20]' : 'text-muted-foreground/30'}`} />)}
                        <span className="text-xs text-muted-foreground ml-1">{r.date}</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground font-body italic">"{r.comment}"</p>
                  </div>
                ))}
              </div>
              <Button className="w-full rounded-full bg-accent hover:bg-accent/90 text-white font-body gap-2" onClick={() => { setSelected(null); toast.success('Export en cours...'); }}>
                <Download className="w-4 h-4" /> Exporter les réponses (CSV)
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* New survey modal */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Nouvelle enquête</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-body font-medium text-muted-foreground mb-1 block">Titre de l'enquête *</label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex: Satisfaction post-séjour Agadir" className="font-body" data-testid="survey-form-title" />
            </div>
            <div>
              <label className="text-xs font-body font-medium text-muted-foreground mb-1 block">Type</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm font-body">
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowNew(false)} className="font-body rounded-full">Annuler</Button>
            <Button onClick={createSurvey} className="bg-accent hover:bg-accent/90 text-white font-body rounded-full" data-testid="survey-form-save">Créer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
